// src/components/ui/GlassButton.jsx
// Premium Glassmorphic Button with chunky rounded border - Aceternity Style

import { motion } from 'framer-motion';
import PropTypes from 'prop-types';

const GlassButton = ({ 
    onClick, 
    children, 
    variant = 'blue', 
    size = 'md',
    disabled = false,
    className = '',
    ariaLabel = ''
}) => {
    const variants = {
        blue: {
            bg: 'linear-gradient(135deg, rgba(59, 130, 246, 0.25) 0%, rgba(99, 102, 241, 0.15) 100%)',
            border: 'rgba(99, 102, 241, 0.5)',
            glow: 'rgba(99, 102, 241, 0.4)',
            text: '#a5b4fc',
            hoverBorder: 'rgba(129, 140, 248, 0.7)',
        },
        green: {
            bg: 'linear-gradient(135deg, rgba(16, 185, 129, 0.25) 0%, rgba(34, 197, 94, 0.15) 100%)',
            border: 'rgba(34, 197, 94, 0.5)',
            glow: 'rgba(34, 197, 94, 0.4)',
            text: '#86efac',
            hoverBorder: 'rgba(74, 222, 128, 0.7)',
        },
        cyan: {
            bg: 'linear-gradient(135deg, rgba(34, 211, 238, 0.2) 0%, rgba(20, 184, 166, 0.12) 100%)',
            border: 'rgba(34, 211, 238, 0.5)',
            glow: 'rgba(34, 211, 238, 0.4)',
            text: '#67e8f9',
            hoverBorder: 'rgba(103, 232, 249, 0.7)',
        },
        purple: {
            bg: 'linear-gradient(135deg, rgba(168, 85, 247, 0.25) 0%, rgba(139, 92, 246, 0.15) 100%)',
            border: 'rgba(168, 85, 247, 0.5)',
            glow: 'rgba(168, 85, 247, 0.4)',
            text: '#c4b5fd',
            hoverBorder: 'rgba(196, 181, 253, 0.7)',
        },
        amber: {
            bg: 'linear-gradient(135deg, rgba(245, 158, 11, 0.25) 0%, rgba(217, 119, 6, 0.15) 100%)',
            border: 'rgba(245, 158, 11, 0.5)',
            glow: 'rgba(245, 158, 11, 0.4)',
            text: '#fcd34d',
            hoverBorder: 'rgba(252, 211, 77, 0.7)',
        },
        pink: {
            bg: 'linear-gradient(135deg, rgba(236, 72, 153, 0.25) 0%, rgba(219, 39, 119, 0.15) 100%)',
            border: 'rgba(236, 72, 153, 0.5)',
            glow: 'rgba(236, 72, 153, 0.4)',
            text: '#f9a8d4',
            hoverBorder: 'rgba(249, 168, 212, 0.7)',
        },
        gray: {
            bg: 'linear-gradient(135deg, rgba(107, 114, 128, 0.2) 0%, rgba(75, 85, 99, 0.12) 100%)',
            border: 'rgba(156, 163, 175, 0.4)',
            glow: 'rgba(156, 163, 175, 0.3)',
            text: '#d1d5db',
            hoverBorder: 'rgba(209, 213, 219, 0.6)',
        },
        indigo: {
            bg: 'linear-gradient(135deg, rgba(99, 102, 241, 0.25) 0%, rgba(79, 70, 229, 0.15) 100%)',
            border: 'rgba(99, 102, 241, 0.5)',
            glow: 'rgba(99, 102, 241, 0.4)',
            text: '#a5b4fc',
            hoverBorder: 'rgba(165, 180, 252, 0.7)',
        },
        orange: {
            bg: 'linear-gradient(135deg, rgba(249, 115, 22, 0.25) 0%, rgba(234, 88, 12, 0.15) 100%)',
            border: 'rgba(249, 115, 22, 0.5)',
            glow: 'rgba(249, 115, 22, 0.4)',
            text: '#fdba74',
            hoverBorder: 'rgba(253, 186, 116, 0.7)',
        },
        emerald: {
            bg: 'linear-gradient(135deg, rgba(16, 185, 129, 0.25) 0%, rgba(5, 150, 105, 0.15) 100%)',
            border: 'rgba(16, 185, 129, 0.5)',
            glow: 'rgba(16, 185, 129, 0.4)',
            text: '#6ee7b7',
            hoverBorder: 'rgba(110, 231, 183, 0.7)',
        },
    };

    const sizes = {
        sm: { padding: '8px 16px', fontSize: '0.75rem' },
        md: { padding: '12px 24px', fontSize: '0.875rem' },
        lg: { padding: '16px 32px', fontSize: '1rem' },
    };

    const v = variants[variant] || variants.blue;
    const s = sizes[size] || sizes.md;

    return (
        <motion.button
            onClick={onClick}
            disabled={disabled}
            aria-label={ariaLabel}
            whileHover={!disabled ? { scale: 1.03, y: -3 } : {}}
            whileTap={!disabled ? { scale: 0.97, y: 0 } : {}}
            className={`group relative cursor-pointer overflow-hidden ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
            style={{
                borderRadius: '50px',
                background: '#0a0a0a',
                border: 'none',
                outline: 'none',
                boxShadow: `
                    inset 0 0.15rem 0.5rem rgba(255, 255, 255, 0.12),
                    inset 0 -0.08rem 0.15rem rgba(0, 0, 0, 0.5),
                    inset 0 -0.25rem 0.5rem rgba(255, 255, 255, 0.15),
                    0 0.4rem 0.8rem rgba(0, 0, 0, 0.35),
                    0 0.15rem 0.3rem rgba(0, 0, 0, 0.25)
                `,
                transition: 'all 0.25s ease',
            }}
        >
            {/* Inner container with gradient and effects */}
            <div
                className="relative overflow-hidden transition-all duration-300"
                style={{
                    padding: s.padding,
                    borderRadius: 'inherit',
                    background: v.bg,
                    border: `2px solid ${v.border}`,
                }}
            >
                {/* Top shine effect */}
                <div
                    className="absolute transition-all duration-300 group-hover:opacity-70 group-hover:-translate-y-0.5"
                    style={{
                        left: '10%',
                        right: '10%',
                        top: '6%',
                        bottom: '55%',
                        borderRadius: '18px 18px 0 0',
                        background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.08) 50%, transparent 100%)',
                        opacity: 0.7,
                    }}
                />
                
                {/* Elliptical glow behind content */}
                <div
                    className="absolute transition-all duration-300 group-hover:-translate-y-1"
                    style={{
                        left: '-25%',
                        right: '-25%',
                        bottom: '35%',
                        top: '-70%',
                        borderRadius: '50%',
                        background: 'rgba(255, 255, 255, 0.06)',
                    }}
                />
                
                {/* Content */}
                <span
                    className="relative z-10 flex items-center justify-center gap-2 font-semibold transition-all duration-200 group-hover:-translate-y-0.5"
                    style={{
                        color: v.text,
                        textShadow: `0 0 20px ${v.glow}`,
                        fontSize: s.fontSize,
                    }}
                >
                    {children}
                </span>
                
                {/* Bottom reflection line */}
                <div
                    className="absolute bottom-0 left-4 right-4 h-px opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{
                        background: `linear-gradient(90deg, transparent, ${v.text}50, transparent)`,
                    }}
                />
            </div>
        </motion.button>
    );
};

// Icon-only glass button
export const GlassIconButton = ({ 
    onClick, 
    children, 
    variant = 'cyan',
    disabled = false,
    className = '',
    ariaLabel = ''
}) => {
    const variants = {
        cyan: {
            bg: 'linear-gradient(135deg, rgba(34, 211, 238, 0.15) 0%, rgba(20, 184, 166, 0.08) 100%)',
            border: 'rgba(34, 211, 238, 0.4)',
            text: '#67e8f9',
        },
        gray: {
            bg: 'linear-gradient(135deg, rgba(107, 114, 128, 0.15) 0%, rgba(75, 85, 99, 0.08) 100%)',
            border: 'rgba(156, 163, 175, 0.35)',
            text: '#9ca3af',
        },
    };

    const v = variants[variant] || variants.cyan;

    return (
        <motion.button
            onClick={onClick}
            disabled={disabled}
            aria-label={ariaLabel}
            whileHover={!disabled ? { scale: 1.05, y: -2 } : {}}
            whileTap={!disabled ? { scale: 0.95, y: 0 } : {}}
            className={`group relative cursor-pointer overflow-hidden ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
            style={{
                borderRadius: '50px',
                background: '#0a0a0a',
                border: 'none',
                outline: 'none',
                boxShadow: `
                    inset 0 0.12rem 0.4rem rgba(255, 255, 255, 0.1),
                    inset 0 -0.06rem 0.12rem rgba(0, 0, 0, 0.4),
                    inset 0 -0.2rem 0.4rem rgba(255, 255, 255, 0.12),
                    0 0.3rem 0.6rem rgba(0, 0, 0, 0.3)
                `,
                transition: 'all 0.25s ease',
            }}
        >
            <div
                className="relative overflow-hidden transition-all duration-300"
                style={{
                    padding: '10px 14px',
                    borderRadius: 'inherit',
                    background: v.bg,
                    border: `2px solid ${v.border}`,
                }}
            >
                {/* Top shine effect */}
                <div
                    className="absolute transition-all duration-300 group-hover:opacity-60"
                    style={{
                        left: '12%',
                        right: '12%',
                        top: '8%',
                        bottom: '58%',
                        borderRadius: '14px 14px 0 0',
                        background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.25) 0%, transparent 100%)',
                        opacity: 0.6,
                    }}
                />
                
                <span 
                    className="relative z-10 flex items-center justify-center transition-colors duration-200"
                    style={{ color: v.text }}
                >
                    {children}
                </span>
            </div>
        </motion.button>
    );
};

GlassButton.propTypes = {
    onClick: PropTypes.func,
    children: PropTypes.node.isRequired,
    variant: PropTypes.oneOf(['blue', 'green', 'cyan', 'purple', 'amber', 'pink', 'gray']),
    size: PropTypes.oneOf(['sm', 'md', 'lg']),
    disabled: PropTypes.bool,
    className: PropTypes.string,
    ariaLabel: PropTypes.string,
};

GlassIconButton.propTypes = {
    onClick: PropTypes.func,
    children: PropTypes.node.isRequired,
    variant: PropTypes.oneOf(['cyan', 'gray']),
    disabled: PropTypes.bool,
    className: PropTypes.string,
    ariaLabel: PropTypes.string,
};

export default GlassButton;
